import {
  DateFormatOptions,
  HabitSchedule,
  ScheduleChangeMode,
  ScheduleEndMode,
  TaskType,
  TimeFormatOptions,
  WeekDays,
} from '@griply/types';
import {
  applyScheduleChangeMode,
  applyScheduleEndMode,
  getScheduleLabel,
  resolveScheduleSubmit,
  ScheduleSubmitAction,
} from '@griply/utils';
import { addDays, subDays } from 'date-fns';
import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Calendar16,
  CalendarDeadline16,
  Check16,
  ClockWatch16,
  Info16,
  Repeat,
  TimerClockFast16,
} from 'shared/assets/icons';
import {
  Button,
  Colors as ButtonColors,
  Sizes as ButtonSizes,
  Variants as ButtonVariants,
} from 'shared/components/ui/button';
import { DateButton } from 'shared/components/ui/date-button';
import { FormDateInput } from 'shared/components/ui/form-date-input';
import { FormDialogHeader } from 'shared/components/ui/form-dialog-header';
import { Icon } from 'shared/components/ui/icon';
import { RepeatForm } from 'shared/components/ui/repeat-rrule-form';
import { ResizableInput } from 'shared/components/ui/resizable-input';
import { TimeField } from 'shared/components/ui/time-field';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useToday } from 'shared/contexts/today';
import { HabitScheduleConfirmChangesDialog } from 'shared/dialogs/habit-schedule-confirm-changes';
import { HabitScheduleConfirmEndDialog } from 'shared/dialogs/habit-schedule-confirm-end';
import { useOpenMenu } from 'shared/hooks/use-open-menu';

import * as Styled from './habit-schedule-form.style';

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;

export type HabitScheduleFormProps = {
  initialValues: HabitSchedule;
  onSubmit: (
    schedule: HabitSchedule,
    splitOptions?: { originalScheduleEndDate: Date },
  ) => void;
  onCancel: () => void;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  timeFormat?: TimeFormatOptions;
  createHabitAllowed: boolean;
  createHabitTargetAllowed: boolean;
  onPremium: () => void;
  minDate?: Date;
  maxDate?: Date;
};

type FormValues = {
  schedule: HabitSchedule; // We store the whole schedule object to keep rrule/freq in sync
  startTime: number | null;
  duration: number | null;
  startDate: Date;
  endDate: Date | null;
};

export const HabitScheduleForm: React.FC<HabitScheduleFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  dateFormat,
  weekStartsOn,
  timeFormat = TimeFormatOptions.Military,
  createHabitAllowed,
  createHabitTargetAllowed,
  onPremium,
  minDate,
  maxDate,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const { control, handleSubmit, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      schedule: initialValues,
      startTime: initialValues.timeslot?.startTime ?? null,
      duration: initialValues.timeslot?.duration ?? null,
      startDate: new Date(initialValues.startDate),
      endDate: initialValues.endDate ? new Date(initialValues.endDate) : null,
    },
  });

  const {
    menuOpen: repeatMenuOpen,
    openMenu: openRepeatMenu,
    closeMenu: closeRepeatMenu,
  } = useOpenMenu();

  const repeatButtonRef = useRef<HTMLDivElement>(null);
  const completionsPerDayToolTipRef = useRef<HTMLDivElement>(null);

  const schedule = watch('schedule');
  const startTime = watch('startTime');
  const duration = watch('duration');
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    schedule: HabitSchedule;
  } | null>(null);

  const [confirmEndDialog, setConfirmEndDialog] = useState<{
    open: boolean;
    schedule: HabitSchedule;
  } | null>(null);

  const onFormSubmit = (data: FormValues) => {
    // Merge updates into the schedule object
    // Note: We're handling mutable updates here potentially, but RHF gives us a clone/state
    const updatedSchedule = {
      ...data.schedule,
      startDate: data.startDate,
      endDate: data.endDate,
      timeslot: {
        startTime: data.startTime,
        duration: data.duration,
      },
    };

    const resolution = resolveScheduleSubmit(initialValues, updatedSchedule);

    if (resolution.action === ScheduleSubmitAction.ConfirmEnd) {
      setConfirmEndDialog({ open: true, schedule: updatedSchedule });
      return;
    }

    if (resolution.action === ScheduleSubmitAction.ConfirmChanges) {
      setConfirmDialog({ open: true, schedule: updatedSchedule });
      return;
    }

    onSubmit(updatedSchedule);
  };

  const handleConfirmChanges = (mode: ScheduleChangeMode) => {
    if (!confirmDialog) {
      return;
    }

    const { schedule, splitOptions } = applyScheduleChangeMode(
      confirmDialog.schedule,
      mode,
      today,
    );

    onSubmit(schedule, splitOptions);
    setConfirmDialog(null);
  };

  const handleScheduleChange = (newSchedule: HabitSchedule | null) => {
    if (newSchedule) {
      // We only want to update the recurrence part (rrule/frequency), not the whole thing if possible

      const mergedSchedule = {
        ...schedule,
        rrule: newSchedule.rrule,
        frequency: newSchedule.frequency,
        completionTarget: newSchedule.completionTarget,
      };

      setValue('schedule', mergedSchedule);
      closeRepeatMenu();
    }
  };

  const handleConfirmEnd = (mode: ScheduleEndMode) => {
    if (!confirmEndDialog) {
      return;
    }

    const { schedule, splitOptions } = applyScheduleEndMode(
      confirmEndDialog.schedule,
      mode,
      today,
    );

    onSubmit(schedule, splitOptions);
    setConfirmEndDialog(null);
  };

  const handleCompletionTargetChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const val = Math.max(parseInt(e.target.value) || 1, 1);
    setValue('schedule', {
      ...schedule,
      completionTarget: {
        ...schedule.completionTarget,
        count: val,
      },
    });
  };

  const handleStartTimeChange = (val: number | null) =>
    setValue('startTime', val ?? null);

  const handleDurationHoursChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const hours = Math.max(0, parseInt(e.target.value || '0', 10));
    const minutes = Math.floor(((duration ?? 0) % MS_PER_HOUR) / MS_PER_MINUTE);
    setValue('duration', hours * MS_PER_HOUR + minutes * MS_PER_MINUTE);
  };

  const handleDurationMinutesChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const hours = Math.floor((duration ?? 0) / MS_PER_HOUR);
    const minutes = Math.max(0, parseInt(e.target.value || '0', 10));
    setValue('duration', hours * MS_PER_HOUR + minutes * MS_PER_MINUTE);
  };

  const handleCloseConfirmDialog = () => setConfirmDialog(null);

  const handleCloseConfirmEndDialog = () => setConfirmEndDialog(null);

  return (
    <Styled.Form onSubmit={handleSubmit(onFormSubmit)}>
      <FormDialogHeader
        onCancel={onCancel}
        title={t('forms.habit.edit.schedule.title')}
        canSubmit
      />
      <Styled.Body>
        <Styled.Section>
          <Styled.Item>
            <Styled.Label>
              <Icon icon={Repeat} />
              <Styled.LabelText>{t('general.labels.repeat')}</Styled.LabelText>
            </Styled.Label>
            <DateButton
              ref={repeatButtonRef}
              dateFormat={dateFormat}
              value={getScheduleLabel(schedule, t, true)}
              onClick={openRepeatMenu}
            />

            {repeatMenuOpen && (
              <RepeatForm
                initialRrule={schedule.rrule?.format}
                initialFrequency={schedule.frequency}
                type={TaskType.Habit}
                weekStartsOn={weekStartsOn}
                onChange={handleScheduleChange}
                isCreateHabitPremium={!createHabitAllowed}
                isTargetPremium={!createHabitTargetAllowed}
                onPremium={onPremium}
                onClose={closeRepeatMenu}
                referenceElement={repeatButtonRef}
                position="bottom-start"
              />
            )}
          </Styled.Item>

          {!schedule.frequency && (
            <Styled.Item>
              <Styled.Label>
                <Icon icon={Check16} />
                <Styled.LabelText>
                  {t('general.labels.completions-per-day')}
                  <Tooltip
                    label={t('general.labels.completions-per-day.tooltip')}
                    referenceElement={completionsPerDayToolTipRef}
                  />
                  <Styled.InfoIcon ref={completionsPerDayToolTipRef}>
                    <Icon icon={Info16} />
                  </Styled.InfoIcon>
                </Styled.LabelText>
              </Styled.Label>
              <ResizableInput
                value={schedule.completionTarget?.count ?? 1}
                onChange={handleCompletionTargetChange}
                showAsFormInput
                type="number"
              />
            </Styled.Item>
          )}
        </Styled.Section>

        <Styled.Divider />

        <Styled.Section>
          <Styled.Item>
            <Styled.Label>
              <Icon icon={Calendar16} />
              <Styled.LabelText>
                {t('general.labels.start-date')}
              </Styled.LabelText>
            </Styled.Label>
            <FormDateInput
              today={today}
              name="startDate"
              control={control}
              dateFormat={dateFormat}
              weekStartsOn={weekStartsOn}
              placeholderText={t('general.labels.select')}
              minDate={minDate}
              maxDate={endDate ? subDays(endDate, 1) : undefined}
              canClear={false}
            />
          </Styled.Item>

          <Styled.Item>
            <Styled.Label>
              <Icon icon={ClockWatch16} />
              <Styled.LabelText>
                {t('general.labels.start-time')}
              </Styled.LabelText>
            </Styled.Label>
            <TimeField
              value={startTime}
              onChange={handleStartTimeChange}
              timeFormat={timeFormat}
              canClearEntry
            />
          </Styled.Item>

          <Styled.Item>
            <Styled.Label>
              <Icon icon={TimerClockFast16} />
              <Styled.LabelText>
                {t('general.labels.duration')}
              </Styled.LabelText>
            </Styled.Label>
            <Styled.DurationFields>
              <ResizableInput
                value={Math.floor((duration ?? 0) / MS_PER_HOUR) || ''}
                onChange={handleDurationHoursChange}
                showAsFormInput
                type="number"
                minWidth="5rem"
                suffix={t('general.labels.time.hours.short')}
                placeholder="0"
              />
              <ResizableInput
                value={
                  Math.floor(((duration ?? 0) % MS_PER_HOUR) / MS_PER_MINUTE) ||
                  ''
                }
                onChange={handleDurationMinutesChange}
                showAsFormInput
                type="number"
                minWidth="5rem"
                suffix={t('general.labels.time.minutes.short')}
                placeholder="0"
              />
            </Styled.DurationFields>
          </Styled.Item>

          <Styled.Item>
            <Styled.Label>
              <Icon icon={CalendarDeadline16} />
              <Styled.LabelText>
                {t('general.labels.end-date')}
              </Styled.LabelText>
            </Styled.Label>
            <FormDateInput
              today={today}
              name="endDate"
              control={control}
              dateFormat={dateFormat}
              weekStartsOn={weekStartsOn}
              placeholderText={t('general.labels.select')}
              minDate={addDays(startDate, 1)}
              maxDate={maxDate}
            />
          </Styled.Item>
        </Styled.Section>
      </Styled.Body>

      <Styled.Interactions>
        <Styled.FormButtons>
          <Button
            variant={ButtonVariants.Contained}
            size={ButtonSizes.Medium}
            color={ButtonColors.White}
            onClick={onCancel}
            type="button"
          >
            {t('general.labels.cancel')}
          </Button>
          <Button size={ButtonSizes.Medium} type="submit">
            {t('general.labels.save')}
          </Button>
        </Styled.FormButtons>
      </Styled.Interactions>
      {confirmDialog && (
        <HabitScheduleConfirmChangesDialog
          open={confirmDialog.open}
          onClose={handleCloseConfirmDialog}
          onConfirm={handleConfirmChanges}
          startDate={initialValues.startDate}
          dateFormat={dateFormat}
        />
      )}

      {confirmEndDialog && (
        <HabitScheduleConfirmEndDialog
          open={confirmEndDialog.open}
          onClose={handleCloseConfirmEndDialog}
          onConfirm={handleConfirmEnd}
          newStartDate={confirmEndDialog.schedule.startDate}
          dateFormat={dateFormat}
        />
      )}
    </Styled.Form>
  );
};
